import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase/config';
import api from './api';

const googleProvider = new GoogleAuthProvider();

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const idToken = await userCredential.user.getIdToken();
    // Verify token with backend
    await api.post('/api/public/auth/verify-token', { id_token: idToken });
    return userCredential.user;
  } catch (error) {
    console.error('Email sign-in error:', error);
    throw error;
  }
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    const idToken = await userCredential.user.getIdToken();
    
    const response = await api.post('/api/public/auth/google-signin', { id_token: idToken });
    
    if (response.status >= 200 && response.status < 300) {
      return userCredential.user;
    }
    
    if (response.status === 401) {
      throw new Error('Authentication failed - please sign in again');
    }
    
    throw new Error(`Failed to sign in with Google. Status: ${response.status}`);
  } catch (error) {
    console.error('Google sign-in error:', error);
    
    // Handle 401 specifically
    if (error.response?.status === 401) {
      await logout();
      throw new Error('Unauthorized access. Please sign in again.');
    }
    
    throw error;
  }
};

// Sign out
export const logout = async () => {
  try {
    // Sign out from Firebase
    await signOut(auth);
    
    // Clear any localStorage items that might be set by Firebase Auth
    localStorage.removeItem('firebase:authUser');
    localStorage.removeItem('firebase:previousUser');
    
    // Clear any other application-specific localStorage items
    // Get all keys and remove any that start with your app's prefix
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sj-affiliate-') || key.includes('firebase')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear sessionStorage as well
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sj-affiliate-') || key.includes('firebase')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Clear cookies that might be related to authentication
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      if (name.includes('firebase') || name.startsWith('sj-affiliate-')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  } catch (error) {
    console.error('Sign-out error:', error);
    throw error;
  }
};

// Subscribe to auth state changes
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Get token for API requests
export const getAuthToken = async () => {
  const user = auth.currentUser;
  if (user) {
    return user.getIdToken();
  }
  return null;
};

// Get user roles from backend or token claims
export const getUserRoles = async () => {
  try {
    // First try to get roles from Firebase token claims
    const user = auth.currentUser;
    if (!user) {
        return {role: '', partnerName: ''};
    }
    
    const response = await api.get('/api/private/me/roles');
    return response.data || {role: '', partnerName: ''};
  } catch (error) {
    console.error('Error getting user roles:', error);
    return {role: '', partnerName: ''}; // Mock data for testing
  }
};